import { type ReactNode, useEffect, useRef, useState } from "react";
import { lenLabel } from "../../assets/library";
import type { LiveNoteUpload } from "../../lib/liveNoteUpload";
import type { NoteApi } from "../../lib/noteUpload";
import { Icon, type IconName } from "../ui/Icon";
import { Select } from "../ui/Select";
import type { Device, DeviceChoice } from "./devices";
import { useVideoRecorder } from "./useVideoRecorder";
import "../../styles/video-recorder.css";

/** What the person does with a finished take: the button's words and icon, and where the file goes. */
export interface RecorderDone {
  label: string;
  icon: IconName;
  /** `live` is the take's upload that began while recording, when the recorder was given one; it is the sender's from here. */
  onUse: (blob: Blob, seconds: number, live: LiveNoteUpload | null) => void;
}

/**
 * The one place a person records a video for the other side: a large preview, the timer against the limit, the
 * camera and microphone to record with, Pause and Resume, then Retake or use it. The host's video note and the
 * guest's "Record a video" both draw it; each side passes its own limit, the notes calls its parts go up with while recording,
 * and what happens to the file.
 */
export function VideoRecorder({ limitSec, done, onClose, upload }: { limitSec: number; done: RecorderDone; onClose: () => void; upload?: NoteApi }): ReactNode {
  const r = useVideoRecorder(limitSec, upload);
  const { status, recorded } = r;
  const [pos, setPos] = useState(0);
  const [playingBack, setPlayingBack] = useState(false);
  const livev = useRef<HTMLVideoElement>(null);
  const back = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (status !== "review" && livev.current) livev.current.srcObject = r.live;
  }, [status, r.live]);

  const close = (
    <button
      type="button"
      className="vr-x"
      aria-label="Close"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <Icon name="x" />
    </button>
  );

  if (r.problem) {
    return (
      <div className="vr" role="group" aria-label="Record a video">
        <div className="vr-st ready">{close}</div>
        <div className="vr-c">
          <p className="vr-msg">{r.problem}</p>
        </div>
      </div>
    );
  }

  const togglePlay = () => {
    const v = back.current;
    if (!v) return;
    if (v.paused) {
      if (v.ended) v.currentTime = 0;
      v.play()
        .then(() => setPlayingBack(true))
        .catch(() => {});
    } else {
      v.pause();
      setPlayingBack(false);
    }
  };
  const retake = () => {
    setPlayingBack(false);
    setPos(0);
    r.retake();
  };
  const len = recorded?.seconds ?? 0;
  const going = status === "rec" || status === "paused";

  return (
    <div className="vr" role="group" aria-label="Record a video">
      <div className={`vr-st ${status}${playingBack ? " playing" : ""}`} onClick={status === "review" ? togglePlay : undefined}>
        {status === "review" && recorded ? (
          <video
            ref={back}
            src={recorded.url}
            playsInline
            onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
            onEnded={() => {
              setPlayingBack(false);
              setPos(len);
            }}
          />
        ) : (
          <video ref={livev} className="vr-cam" muted playsInline autoPlay aria-label="Your camera" />
        )}
        {going ? (
          <span className="vr-t">
            <i />
            <span>
              {lenLabel(r.shown)} / {lenLabel(limitSec)}
            </span>
          </span>
        ) : status === "review" ? (
          <span className="vr-t">
            <span>{playingBack ? `${lenLabel(pos)} / ${lenLabel(len)}` : lenLabel(len)}</span>
          </span>
        ) : null}
        {status === "review" ? (
          <>
            <button
              type="button"
              className="vr-play"
              aria-label={playingBack ? "Pause" : "Play"}
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
              }}
            >
              <Icon name={playingBack ? "pause" : "playf"} fill={!playingBack} />
            </button>
            <span className="vr-pb">
              <i style={{ width: `${len ? (pos / len) * 100 : 0}%` }} />
            </span>
          </>
        ) : null}
        {status === "ready" && r.ready ? (
          <div className="vr-pick">
            <Pick kind="camera" icon="video" label="Camera" list={r.devices.cameras} value={r.chosen.camera} onPick={r.pick} />
            <Pick kind="mic" icon="mic" label="Microphone" list={r.devices.mics} value={r.chosen.mic} onPick={r.pick} />
          </div>
        ) : null}
        {close}
      </div>
      <div className={`vr-c${going ? " going" : ""}`}>
        {status === "review" ? (
          <>
            <button type="button" className="vr-btn" onClick={retake}>
              <Icon name="undo" />
              Retake
            </button>
            <button type="button" className="vr-btn p" onClick={() => recorded && done.onUse(recorded.blob, recorded.seconds, r.hand())}>
              <Icon name={done.icon} />
              {done.label}
            </button>
          </>
        ) : going ? (
          <>
            <button type="button" className="vr-btn vr-pause" onClick={status === "paused" ? r.resume : r.pause}>
              <Icon name={status === "paused" ? "playf" : "pause"} fill={status === "paused"} />
              {status === "paused" ? "Resume" : "Pause"}
            </button>
            <button type="button" className="vr-go on" aria-label="Stop recording" onClick={r.stop}>
              <i />
            </button>
            <span />
          </>
        ) : (
          <button type="button" className="vr-go" disabled={!r.ready} aria-label="Start recording" onClick={r.start}>
            <i />
          </button>
        )}
      </div>
    </div>
  );
}

/** One device: its name when there is only one to use, a list when there are several, nothing when there are none. */
function Pick({
  kind,
  icon,
  label,
  list,
  value,
  onPick,
}: {
  kind: keyof DeviceChoice;
  icon: IconName;
  label: string;
  list: Device[];
  value: string | undefined;
  onPick: (kind: keyof DeviceChoice, id: string) => void;
}): ReactNode {
  if (list.length === 0) return null;
  if (list.length === 1) {
    return (
      <span className="vr-chip" title={label}>
        <Icon name={icon} />
        <span className="vr-name">{list[0].label}</span>
      </span>
    );
  }
  return (
    <span className="vr-chip">
      <Icon name={icon} />
      <Select
        className="vr-sel"
        ariaLabel={label}
        value={value ?? list[0].id}
        options={list.map((d) => ({ value: d.id, label: d.label }))}
        onChange={(id) => onPick(kind, id)}
      />
    </span>
  );
}
